import { css, cx } from "@linaria/core"

import * as textStyles from "@/styles/textStyles.ts"

import type { HTMLAttributes } from "react"


type BadgeKind = "error" | "warning" | "success" | "info" | "disabled"
type BadgeVariant = "default" | "small"
type BadgeTextTransform = "none" | "uppercase" | "lowercase" | "capitalize"

type BadgeProps = HTMLAttributes<HTMLDivElement> & {
  kind: BadgeKind
  variant?: BadgeVariant
  textTransform?: BadgeTextTransform
}

export const Badge = ({ className, variant = "default", textTransform = "none", ...props }: BadgeProps) => {
  const classNames = cx(
    styles.Badge,
    variant === "small" && styles.Small,
    getKindClassName(props.kind),
    getTextTransformClassName(textTransform),
    className
  )

  return (
    <span className={classNames}>{props.children}</span>
  )
}

function getKindClassName(kind: BadgeKind) {
  switch (kind) {
    case "error":
      return styles.Error
    case "warning":
      return styles.Warning
    case "success":
      return styles.Success
    case "info":
      return styles.Info
    case "disabled":
      return styles.Disabled
  }
}

function getTextTransformClassName(textTransform: BadgeTextTransform) {
  switch (textTransform) {
    case "uppercase":
      return styles.Uppercase
    case "lowercase":
      return styles.Lowercase
    case "capitalize":
      return styles.Capitalize
  }
}

const styles = {
  Badge: css`
    display: inline-flex;
    align-items: center;
    gap: 4px;

    padding: 2px 6px;
    border-radius: var(--border-radius-4);

    ${textStyles.smallestFontSize};

    font-weight: 600;
    white-space: nowrap;
  `,

  Small: css`
    padding: 3px 6px;
    border-radius: var(--border-radius-8);
    font-size: 9px;
    font-weight: 500;
  `,

  Error: css`
    color: var(--error-color);
    background-color: var(--error-color-10);
  `,

  Warning: css`
    color: var(--warning-color);
    background-color: var(--warning-color-10);
  `,

  Success: css`
    color: var(--success-color);
    background-color: var(--success-color-10);
  `,

  Info: css`
    color: var(--main-color);
    background-color: var(--main-color-10);
  `,

  Disabled: css`
    color: var(--secondary-text-color);
    background-color: var(--text-color-10);
  `,

  Uppercase: css`
    text-transform: uppercase;
  `,

  Lowercase: css`
    text-transform: lowercase;
  `,

  Capitalize: css`
    text-transform: capitalize;
  `,
}
